import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvironmentService } from '@app/core/services/environment.service';
import { PagedData } from '@app/modules/lookup/models/paged-data.model';
import { Observable } from 'rxjs';
import { Report, ReportDataSource, ReportStatus, ReportType } from '../models/report-designer.model';

const headers = new HttpHeaders({
  'Content-Type': 'application/json',
});

@Injectable({
  providedIn: 'root'
})
export class ReportDesignerService {

  constructor(private http: HttpClient, private envService: EnvironmentService) { }

  private get reportDesignerBaseUrl(): string {
    return `${this.envService.env.apiUrl}`;
  }

  getReports(take?: number, skip?: number, sort?: string, filter?: string): Observable<PagedData<Report>> {
    let params = new HttpParams();
    sort ? params = params.append('sort', sort) : null;
    filter ? params = params.append('filter', filter) : null;
    skip !== undefined && skip !== null ? params = params.append('skip', skip.toString()) : null;
    take !== undefined && take !== null ? params = params.append('take', take.toString()) : null;

    return this.http.get<PagedData<Report>>(`${this.reportDesignerBaseUrl}Reports`, {
      headers: headers,
      params: params
    });
  }

  async getAllReports(sort?: string, filter?: string): Promise<PagedData<Report>> {
    let take = 100;
    let skip = 0;
    let allData = {
      skip: null,
      take: null,
      totalPages: null,
      total: null,
      data: []
    };

    while (allData.total === null || skip < allData.total) {
      const res = await this.getReports(take, skip, sort, filter).toPromise();

      if (res && res.data && res.data.length > 0) {
        allData.data = allData.data.concat(res.data);
        skip += take;
        allData.total = res.total;
      } else {
        break;
      }
    }

    return allData;
  }

  getReport(id: string): Observable<Report> {
    return this.http.get<Report>(`${this.reportDesignerBaseUrl}Reports/${id}`, {
      headers: headers
    });
  }

  createReport(report: any): Observable<any> {
    return this.http.post<any>(`${this.reportDesignerBaseUrl}Reports`, report, {
      headers: headers
    });
  }

  updateReport(id: string, report: any): Observable<any> {
    return this.http.put<any>(`${this.reportDesignerBaseUrl}Reports/${id}`, report, {
      headers: headers
    });
  }

  changeReportStatus(id: string, reportStatusId: string): Observable<any> {
    return this.http.put<any>(`${this.reportDesignerBaseUrl}Reports/${id}/Status`, { reportStatusId }, {
      headers: headers
    });
  }

  deleteReport(id: string): Observable<any> {
    return this.http.delete<any>(`${this.reportDesignerBaseUrl}Reports/${id}`, {
      headers: headers
    });
  }

  getReportDataSources(take?: number, skip?: number, sort?: string, filter?: string): Observable<PagedData<ReportDataSource>> {
    let params = new HttpParams();
    sort ? params = params.append('sort', sort) : null;
    filter ? params = params.append('filter', filter) : null;
    skip !== undefined && skip !== null ? params = params.append('skip', skip.toString()) : null;
    take !== undefined && take !== null ? params = params.append('take', take.toString()) : null;

    return this.http.get<PagedData<ReportDataSource>>(`${this.reportDesignerBaseUrl}Reports/DataSources`, {
      headers: headers,
      params: params
    });
  }

  async getAllReportDataSources(sort?: string, filter?: string): Promise<PagedData<ReportDataSource>> {
    let take = 50;
    let skip = 0;
    let allData = {
      skip: null,
      take: null,
      totalPages: null,
      total: null,
      data: []
    };

    while (allData.total === null || skip < allData.total) {
      const res = await this.getReportDataSources(take, skip, sort, filter).toPromise();

      if (res && res.data && res.data.length > 0) {
        allData.data = allData.data.concat(res.data);
        skip += take;
        allData.total = res.total;
      } else {
        break;
      }
    }

    return allData;
  }

  getReportTypes(take?: number, skip?: number, sort?: string, filter?: string): Observable<PagedData<ReportType>> {
    let params = new HttpParams();
    sort ? params = params.append('sort', sort) : null;
    filter ? params = params.append('filter', filter) : null;
    skip !== undefined && skip !== null ? params = params.append('skip', skip.toString()) : null;
    take !== undefined && take !== null ? params = params.append('take', take.toString()) : null;

    return this.http.get<PagedData<ReportType>>(`${this.reportDesignerBaseUrl}Reports/Types`, {
      headers: headers,
      params: params
    });
  }

  getReportStatuses(): Observable<PagedData<ReportStatus>> {
    return this.http.get<PagedData<ReportStatus>>(`${this.reportDesignerBaseUrl}Reports/Statuses`, {
      headers: headers
    });
  }

  exportReport(id: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.reportDesignerBaseUrl}Reports/${id}/Export`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  importReport(file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.post<any>(`${this.reportDesignerBaseUrl}Reports/Import`, formData, {
      headers: {}
    });
  }
}
